import { usePrevious } from 'react-use';
import { useLayoutEffect } from 'react';

import { visitPaths } from '~libs.desktop.shared.paths';

import {
   FILTER_SEPARATOR,
   getAppointmentConfirmationStatusFilterSearchParamValue,
   getAppointmentConfirmationStatusFromSearchParam,
   mapAppointmentConfirmationStatusFilterParamToAppointmentConfirmationStatus,
} from './AppointmentConfirmationStatusField.utils';
import {
   AppointmentConfirmationStatus,
   AppointmentConfirmationStatusFilterParam,
} from './AppointmentConfirmationStatusField.types';

const STORAGE_PREFIX = 'egabinet-desktop:';
const APPOINTMENT_CONFIRMATION_STATUS_FILTER_STORAGE_KEY = `${STORAGE_PREFIX}appointmentConfirmationStatus`;

type SetFilter = (filter: AppointmentConfirmationStatusFilterParam) => void;
type DeleteFilter = VoidFunction;
type GetStoredAppointmentConfirmationStatuses = () => AppointmentConfirmationStatus[];

type AppointmentConfirmationStatusFilterStorageReturnFn = {
   setFilter: SetFilter;
   deleteFilter: DeleteFilter;
   getStoredConfirmationStatuses: GetStoredAppointmentConfirmationStatuses;
};

export const appointmentConfirmationStatusFilterStorage: AppointmentConfirmationStatusFilterStorageReturnFn = (() => {
   const deleteFilter: DeleteFilter = () =>
      window.sessionStorage.removeItem(APPOINTMENT_CONFIRMATION_STATUS_FILTER_STORAGE_KEY);
   const setFilter: SetFilter = (filter) => {
      if (filter === '') {
         return deleteFilter();
      }

      return window.sessionStorage.setItem(APPOINTMENT_CONFIRMATION_STATUS_FILTER_STORAGE_KEY, filter);
   };

   const getStoredAppointmentConfirmationStatuses: GetStoredAppointmentConfirmationStatuses = () =>
      mapAppointmentConfirmationStatusFilterParamToAppointmentConfirmationStatus(
         window.sessionStorage.getItem(APPOINTMENT_CONFIRMATION_STATUS_FILTER_STORAGE_KEY)
      );

   return {
      setFilter: setFilter,
      getStoredConfirmationStatuses: getStoredAppointmentConfirmationStatuses,
      deleteFilter: deleteFilter,
   };
})();

export const useSyncAppointmentConfirmationStatusFilterParamWithStorage = (
   searchParams: URLSearchParams,
   onSync: (searchParams: URLSearchParams) => void
): void => {
   const appointmentConfirmationStatusFilterParam = searchParams.get(
      visitPaths.APPOINTMENT_CONFIRMATION_STATUS_SEARCH_PARAM
   );
   const previousAppointmentConfirmationStatusFilterParam = usePrevious(appointmentConfirmationStatusFilterParam);
   const memoizedAppointmentConfirmationStatusFilter = appointmentConfirmationStatusFilterStorage.getStoredConfirmationStatuses();

   useLayoutEffect(() => {
      if (
         appointmentConfirmationStatusFilterParam &&
         appointmentConfirmationStatusFilterParam !== previousAppointmentConfirmationStatusFilterParam
      ) {
         const appointmentConfirmationStatusesFromSearchParam = appointmentConfirmationStatusFilterParam
            .split(FILTER_SEPARATOR)
            .map(Number);
         const appointmentConfirmationStatusesValid = appointmentConfirmationStatusesFromSearchParam.every(
            (id) => !isNaN(id)
         );

         if (!appointmentConfirmationStatusesValid) {
            searchParams.delete(visitPaths.APPOINTMENT_CONFIRMATION_STATUS_SEARCH_PARAM);

            onSync(searchParams);
         }

         if (appointmentConfirmationStatusesValid) {
            appointmentConfirmationStatusFilterStorage.setFilter(appointmentConfirmationStatusFilterParam);
         }
      }
      if (memoizedAppointmentConfirmationStatusFilter.length) {
         const appointmentConfirmationStatusesFromSearchParam = appointmentConfirmationStatusFilterParam
            ?.split(FILTER_SEPARATOR)
            .map(Number);
         const memoizedStatusesAsSet = new Set(memoizedAppointmentConfirmationStatusFilter);
         if (
            !appointmentConfirmationStatusesFromSearchParam ||
            appointmentConfirmationStatusesFromSearchParam.some((searchParamValue) => {
               const statusFromSearchParam = getAppointmentConfirmationStatusFromSearchParam(searchParamValue);
               return !statusFromSearchParam || !memoizedStatusesAsSet.has(statusFromSearchParam);
            })
         ) {
            const appointmentConfirmationStatusFilterParam = getAppointmentConfirmationStatusFilterSearchParamValue(
               memoizedAppointmentConfirmationStatusFilter
            );
            searchParams.set(
               visitPaths.APPOINTMENT_CONFIRMATION_STATUS_SEARCH_PARAM,
               appointmentConfirmationStatusFilterParam
            );

            onSync(searchParams);
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [appointmentConfirmationStatusFilterParam, memoizedAppointmentConfirmationStatusFilter]);
};
